import { Icon, ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Menu, MenuProps } from 'components/Menu';
import { Pressable } from 'components/Pressable';
import { TEXT_SIZE_NORMAL, Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { FontFamilyRegular, theme } from 'lib/theme';
import { useCallbackState } from 'lib/useCallbackState';
import React, { Ref, useEffect, useRef } from 'react';
import {
  BlurEvent,
  InputModeOptions,
  KeyboardTypeOptions,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputKeyPressEvent,
  TextInputProps,
  TextInputSubmitEditingEvent,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { formatCurrency } from 'lib/riskbase';
import { toInteger } from 'lodash';
import { FieldTypeIcon } from './FieldTypeIcon';
import { Button, ButtonTheme } from 'components/Button';

export enum TextFieldSpecial {
  'currency' = 'currency',
  'number' = 'number',
}

type AutoComplete =
  | 'email'
  | 'password'
  | 'newPassword'
  | 'phone'
  | 'addressLine1'
  | 'addressLine2'
  | 'addressCity'
  | 'addressCounty'
  | 'addressPostcode'
  | 'url'
  | 'off';

const AutoCompleteTranslation: Record<
  AutoComplete,
  TextInputProps['autoComplete']
> = {
  email: 'email',
  phone: 'tel',
  url: 'url',
  addressCounty: 'postal-address-region',
  password: 'current-password',
  newPassword: 'new-password',
  addressLine1: 'address-line1',
  addressLine2: 'address-line2',
  addressCity: 'postal-address-locality',
  addressPostcode: 'postal-address',
  off: 'off',
};

const InputModeToKeyboardType: Record<InputModeOptions, KeyboardTypeOptions> = {
  decimal: 'decimal-pad',
  email: 'email-address',
  numeric: 'number-pad',
  search: 'web-search',
  tel: 'phone-pad',
  text: 'ascii-capable',
  url: 'url',
  none: 'default',
};

type Props = {
  autoComplete?: AutoComplete;
  value?: string;
  placeholder?: string;
  // keyboardType?: TextInputProps['keyboardType'];
  inputMode?: TextInputProps['inputMode'];
  autoCapitalize?: TextInputProps['autoCapitalize'];
  inputStyle?: StyleProp<TextStyle>;
  autoCorrect?: TextInputProps['autoCorrect'];
  autoFocus?: TextInputProps['autoFocus'];
  autoFocusWeb?: TextInputProps['autoFocus'];
  autoFocusNative?: TextInputProps['autoFocus'];
  autoFocusDelay?: number | boolean;
  returnKeyType?: TextInputProps['returnKeyType'];
  onSubmitEditing?: TextInputProps['onSubmitEditing'];
  onDelete?: () => void;
  onDrag?: () => void;
  onBlur?: TextInputProps['onBlur'];
  onEndEditing?: TextInputProps['onEndEditing'];
  onFocus?: TextInputProps['onFocus'];
  selectTextOnFocus?: TextInputProps['selectTextOnFocus'];
  clearTextOnFocus?: TextInputProps['clearTextOnFocus'];
  enablesReturnKeyAutomatically?: TextInputProps['enablesReturnKeyAutomatically'];
  secureTextEntry?: TextInputProps['secureTextEntry'];
  onChangeDebounceMs?: number;
  autoFocusEveryRender?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  textRight?: string;
  textRightStyle?: StyleProp<TextStyle>;
  textRightContainerStyle?: StyleProp<ViewStyle>;
  showTextIcon?: boolean;
  clearable?: boolean;
  iconLeft?: ICONS;
  textLeft?: string | React.ReactNode;
  onTextLeftPress?: () => void;
  textLeftStyle?: StyleProp<TextStyle>;
  textLeftPressableStyle?: StyleProp<ViewStyle>;
  onAction?: null | (() => void);
  actionIcon?: ICONS;
  indentActions?: boolean;
  menuOptions?: MenuProps['options'];
  menuIcon?: ICONS;
  textIcon?: ICONS;
  onEsc?: () => void;
  rbs?: boolean;
  syncIncomingValue?: boolean;
  actionsMarginRight?: number;
  disabled?: boolean;
  maxLength?: number;
  special?: TextFieldSpecial;
  myRef?: Ref<TextFieldRef>;
  maxNumberValue?: number;
  // label is not visible but is used for accessibility
  label?: string;
  // required is not visible but is used for accessibility
  required?: boolean;
  // true to display a submit button when dirty - delay onChange until it is pressed
  enableManualOnChange?: boolean;
};

type PropsWithOnChange = {
  name?: never;
  onChange: (value: string) => void;
  onChangeWithName?: never;
};

type PropsWithOnChangeWithName<K extends string> = {
  name: K;
  onChange?: never;
  onChangeWithName: (key: K, value: string) => void;
};

const getReturnValue = (value: string, special?: TextFieldSpecial): string => {
  if (special === TextFieldSpecial.currency) {
    const floatValue = parseFloat(value);
    return Number.isNaN(floatValue) ? '' : (floatValue * 100).toFixed();
  }
  return value;
};

const getInputValue = (
  value: undefined | string,
  special?: TextFieldSpecial,
): string => {
  if (
    value !== undefined &&
    value !== '' &&
    special === TextFieldSpecial.currency
  ) {
    return formatCurrency(parseInt(value, 10), false, false);
  }
  return value !== undefined ? value : '';
};

export type TextFieldRef = {
  focus: () => void;
};

export const TextField = <K extends string>({
  autoComplete,
  autoCapitalize,
  autoCorrect,
  autoFocus,
  autoFocusWeb,
  autoFocusNative,
  autoFocusDelay,
  autoFocusEveryRender,
  clearable = false,
  containerStyle,
  enablesReturnKeyAutomatically,
  showTextIcon,
  iconLeft,
  inputStyle,
  inputMode,
  name,
  onBlur,
  onEndEditing,
  onChange,
  enableManualOnChange,
  onFocus,
  onChangeDebounceMs = 200,
  onChangeWithName,
  onDelete,
  onDrag,
  onSubmitEditing,
  onTextLeftPress,
  selectTextOnFocus,
  clearTextOnFocus,
  placeholder,
  returnKeyType,
  secureTextEntry,
  textLeft,
  textLeftPressableStyle,
  textLeftStyle,
  textRight,
  textRightStyle,
  textRightContainerStyle,
  value,
  onAction,
  actionIcon = ICONS.HandPointerLight,
  indentActions,
  actionsMarginRight,
  menuOptions,
  menuIcon,
  textIcon = ICONS.InputTextDuo,
  onEsc,
  rbs,
  syncIncomingValue,
  disabled,
  maxLength,
  special,
  myRef,
  maxNumberValue,
  label,
  required,
}: Props &
  (PropsWithOnChange | PropsWithOnChangeWithName<K>)): React.ReactElement => {
  const inputRef = useRef<TextInput>(null);
  const didFocusRef = useRef(false);
  const focusTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const autoFocusFinal =
    Platform.OS === 'web'
      ? autoFocusWeb !== undefined
        ? autoFocusWeb
        : autoFocus
      : autoFocusNative !== undefined
        ? autoFocusNative
        : autoFocus;

  const [secureInput, setSecureInput] = React.useState<boolean | undefined>(
    secureTextEntry ? true : undefined,
  );

  const formattedIncomingValue = React.useMemo(
    () => getInputValue(value, special),
    [special, value],
  );
  const lastIncomingValueRef = React.useRef(formattedIncomingValue);
  const internalValueRef = React.useRef(formattedIncomingValue);
  const returnedValueRef = React.useRef(formattedIncomingValue);
  const valueIsPendingReturnRef = React.useRef<boolean>(false);

  React.useImperativeHandle(
    myRef,
    () => ({
      focus: () => inputRef.current?.focus(),
    }),
    [],
  );

  const makeChange = React.useCallback(() => {
    if (onChange) {
      onChange(internalValueRef.current);
    } else if (name) {
      onChangeWithName(name, internalValueRef.current);
    }
    returnedValueRef.current = internalValueRef.current;
    valueIsPendingReturnRef.current = false;
  }, [name, onChange, onChangeWithName]);

  const handleChange = React.useCallback(() => {
    if (!enableManualOnChange) {
      makeChange();
    }
  }, [enableManualOnChange, makeChange]);

  const [currentValue, setCurrentValue, setCurrentValueDirect] =
    useCallbackState<string>(
      formattedIncomingValue,
      handleChange,
      onChangeDebounceMs,
    );

  const setValue = React.useCallback(
    (newValue: string, options?: { forceInstantChange: boolean }) => {
      let finalValue = newValue;
      if (special) {
        if (special === TextFieldSpecial.number) {
          finalValue = newValue.replace(/[^0-9]/gim, '');
          if (
            maxNumberValue !== undefined &&
            toInteger(finalValue) > maxNumberValue
          ) {
            finalValue = `${maxNumberValue}`;
          }
        } else if (special === TextFieldSpecial.currency) {
          const [newWholeValue, newPointValue] = newValue
            .replace(/[^0-9.]/gim, '')
            .toString()
            .split('.', 2);
          finalValue =
            newPointValue !== undefined
              ? `${newWholeValue}.${newPointValue.substring(0, 2)}`
              : `${newWholeValue}`;
        }
      }
      internalValueRef.current = getReturnValue(finalValue, special);
      if (options?.forceInstantChange) {
        setCurrentValueDirect(finalValue);
        makeChange();
      } else {
        setCurrentValue(finalValue);
      }
    },
    [
      makeChange,
      maxNumberValue,
      setCurrentValue,
      setCurrentValueDirect,
      special,
    ],
  );

  valueIsPendingReturnRef.current =
    returnedValueRef.current !== internalValueRef.current;
  const valueIsPendingReturn = valueIsPendingReturnRef.current;

  const handleBlur = React.useCallback(
    (event: BlurEvent) => {
      if (valueIsPendingReturnRef.current) {
        handleChange();
      }
      onBlur?.(event);
    },
    [handleChange, onBlur],
  );

  const handleFocus = React.useCallback(() => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
    didFocusRef.current = true;
    inputRef.current?.focus();
  }, []);

  const handleSubmitEditing = React.useCallback(
    (event: TextInputSubmitEditingEvent) => {
      if (onSubmitEditing) {
        onSubmitEditing(event);
      } else if (valueIsPendingReturnRef.current) {
        makeChange();
      }
      // Keep focus
      if (Platform.OS === 'web') {
        setTimeout(handleFocus, 1);
      }
    },
    [handleFocus, makeChange, onSubmitEditing],
  );

  const handleClear = React.useCallback(() => {
    setValue('', { forceInstantChange: true });
    handleFocus();
  }, [handleFocus, setValue]);

  useEffect(() => {
    if (!didFocusRef.current && autoFocusFinal) {
      if (autoFocusDelay) {
        focusTimeoutRef.current = setTimeout(
          handleFocus,
          autoFocusDelay === true ? 200 : autoFocusDelay,
        );
      } else {
        handleFocus();
      }
    }
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
    };
  }, [autoFocusFinal, autoFocusDelay, handleFocus]);

  useEffect(() => {
    if (autoFocusEveryRender) {
      handleFocus();
    }
  });

  // Force new incoming value if syncIncomingValue=true
  const lastIncomingValue = lastIncomingValueRef.current;
  lastIncomingValueRef.current = formattedIncomingValue;
  useEffect(() => {
    if (
      // syncIncomingValue is enabled
      syncIncomingValue &&
      // there is no pending return
      !valueIsPendingReturnRef.current &&
      // the last incoming value is different to the formatted incoming value
      lastIncomingValue !== formattedIncomingValue
    ) {
      if (formattedIncomingValue !== returnedValueRef.current) {
        setValue(formattedIncomingValue);
      } else {
        lastIncomingValueRef.current = returnedValueRef.current;
      }
    }
  }, [
    currentValue,
    setValue,
    syncIncomingValue,
    formattedIncomingValue,
    lastIncomingValue,
  ]);

  const showClearButton = !!currentValue && clearable;
  const showSubmitButton = enableManualOnChange && valueIsPendingReturn;
  const hasRightIcon =
    onDrag || showSubmitButton || showClearButton || secureInput !== undefined;

  const handleKeyPress = React.useCallback(
    (event: TextInputKeyPressEvent) => {
      if (onEsc && event.nativeEvent.key === 'Escape') {
        onEsc();
      }
    },
    [onEsc],
  );

  const RightMemo = React.useMemo(
    () =>
      (showSubmitButton ||
        showClearButton ||
        onAction !== undefined ||
        menuOptions?.length ||
        onDrag ||
        showTextIcon) && (
        <ViewWithSeparators
          horizontal
          separatorMargin={2}
          separatorColor={'transparent'}
          style={{
            marginRight:
              actionsMarginRight !== undefined
                ? actionsMarginRight
                : indentActions
                  ? theme.paddingHorizontal
                  : theme.paddingHorizontal / 2,
            marginLeft: 4,
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'stretch',
          }}
        >
          {showSubmitButton && (
            <Button
              text="Search"
              compact
              onPress={makeChange}
              buttonTheme={ButtonTheme.themeless}
            />
          )}
          {showClearButton && (
            <IconButton
              icon={ICONS.XmarkLight}
              iconSize={16}
              size={24}
              onPress={handleClear}
              disabled={disabled}
            />
          )}
          {!!menuOptions?.length && (
            <Menu
              options={menuOptions}
              iconButtonConfig={{
                icon: menuIcon || ICONS.EllipsisLight,
                iconSize: 16,
                size: 24,
              }}
            />
          )}
          {onAction !== undefined && (
            <IconButton
              icon={actionIcon}
              onPress={onAction || undefined}
              disabled={disabled || onAction === null}
            />
          )}
          {onDrag && (
            <IconButton
              onPressIn={onDrag}
              icon={ICONS.GripLinesLight}
              color="black"
            />
          )}
          {showTextIcon && <FieldTypeIcon icon={textIcon} />}
        </ViewWithSeparators>
      ),
    [
      showSubmitButton,
      showClearButton,
      onAction,
      menuOptions,
      onDrag,
      showTextIcon,
      actionsMarginRight,
      indentActions,
      makeChange,
      handleClear,
      disabled,
      menuIcon,
      actionIcon,
      textIcon,
    ],
  );

  const finalInputMode: TextInputProps['inputMode'] =
    special === TextFieldSpecial.currency ? 'decimal' : inputMode;
  const finalKeyboardType: TextInputProps['keyboardType'] = finalInputMode
    ? InputModeToKeyboardType[finalInputMode]
    : undefined;

  return (
    <View
      style={[
        styles.container,
        disabled ? styles.containerDisabled : undefined,
        containerStyle,
      ]}
    >
      {rbs && (
        <View
          style={{
            backgroundColor: theme.colors.support30,
            width: 4,
            height: 4,
            borderRadius: 2,
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 9,
          }}
        />
      )}
      {iconLeft && (
        <Pressable
          style={styles.navigationIconContainer}
          onPress={handleFocus}
          disabled={disabled}
        >
          <Icon source={iconLeft} size={19} />
        </Pressable>
      )}
      {onDelete && (
        <Pressable
          style={styles.iconLeftPressable}
          onPress={onDelete}
          disabled={disabled}
        >
          <Icon
            source={ICONS.CircleMinusSolid}
            size={19}
            color={theme.colors.red}
          />
        </Pressable>
      )}
      {!!textLeft && (
        <Pressable
          onPress={onTextLeftPress || handleFocus}
          accessible={false}
          style={[
            { marginLeft: theme.paddingHorizontal },
            textLeftPressableStyle,
          ]}
          focusable={false}
          tabIndex={-1}
        >
          <Txt style={[styles.textLeft, textLeftStyle]}>{textLeft}</Txt>
        </Pressable>
      )}
      <TextInput
        maxLength={maxLength}
        editable={!disabled}
        autoCapitalize={autoCapitalize}
        placeholderTextColor={theme.colors.placeholder}
        placeholder={placeholder}
        style={[
          styles.input,
          Platform.OS === 'web' && styles.inputWeb,
          inputStyle,
          onDelete || iconLeft ? { paddingLeft: 0 } : undefined,
          hasRightIcon ? { paddingRight: 0 } : undefined,
        ]}
        // Auto complete, auto correct, keyboard type
        autoComplete={
          autoComplete ? AutoCompleteTranslation[autoComplete] : undefined
        }
        autoCorrect={autoCorrect}
        keyboardType={finalKeyboardType}
        inputMode={finalInputMode}
        value={currentValue || ''}
        onChangeText={setValue}
        clearButtonMode="never"
        enterKeyHint="search"
        onSubmitEditing={handleSubmitEditing}
        onBlur={handleBlur}
        onEndEditing={onEndEditing}
        onFocus={onFocus}
        onKeyPress={onEsc ? handleKeyPress : undefined}
        selectTextOnFocus={selectTextOnFocus}
        clearTextOnFocus={clearTextOnFocus}
        returnKeyType={returnKeyType}
        enablesReturnKeyAutomatically={enablesReturnKeyAutomatically}
        secureTextEntry={secureInput}
        ref={inputRef}
        accessibilityLabel={label + (required ? ' (Required)' : '')}
      />
      {secureInput !== undefined && (
        <IconButton
          style={styles.toggleSecureButton}
          icon={secureInput ? ICONS.EyeLight : ICONS.EyeSlashLight}
          onPress={() =>
            setSecureInput((currentSecureInput) => !currentSecureInput)
          }
          size={19}
        />
      )}
      {RightMemo}
      {!!textRight && (
        <View style={textRightContainerStyle}>
          <Txt style={[styles.textRight, textRightStyle]}>{textRight}</Txt>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: 'white',
    flexDirection: 'row',
  },
  containerDisabled: {
    opacity: 0.42,
    pointerEvents: 'none',
  },
  iconLeftPressable: {
    marginLeft: theme.paddingHorizontal - 6,
    marginRight: 6,
    padding: 6,
  },
  input: {
    backgroundColor: 'transparent',
    color: theme.colors.text,
    flex: 1,
    width: '100%',
    fontFamily: FontFamilyRegular,
    fontSize: TEXT_SIZE_NORMAL,
    minHeight: 50,
    paddingHorizontal: theme.paddingHorizontal,
  },
  inputWeb: {
    // @ts-expect-error none exists in web
    outlineStyle: 'none',
  },
  navigationIconContainer: {
    height: 36,
    width: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 0,
    marginRight: 0,
  },
  textLeft: {
    color: 'rgba(0,0,0,0.54)',
  },
  textRight: {
    color: 'rgba(0,0,0,0.54)',
    textAlign: 'right',
  },
  toggleSecureButton: {
    position: 'absolute',
    right: theme.paddingHorizontal,
  },
});
